import type { ChangeEvent, MouseEvent } from 'react'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { keepPreviousData } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { IconEmail } from '@gto/ui/assets'
import { Button } from '@gto/ui/button'
import { InformationCircleIcon } from '@gto/ui/icons/outline'
import { Typography } from '@gto/ui/typography'

import {
  type EmailChangeRequest,
  useAccountEmailChangeConfirmCreate,
  useAccountEmailChangeCreate,
  useAccountEmailVerifyResendCreate,
  useAccountRetrieve,
} from '@/api/gen'
import { FormContainer } from '@/components/form-container'
import { Loader } from '@/components/loader'
import { TextInput } from '@/components/text-input'
import { useAuth } from '@/hooks/use-auth'
import { useFormValidation } from '@/hooks/use-form-validation'
import { getAxiosErrorMessage } from '@/utils/helpers'
import type { PasswordResetFormField } from '@/utils/validation'
import { passwordResetSchema } from '@/utils/validation'

import { EmailValidationError } from './email-validation-error'
import { EmailValidationSuccess } from './email-validation-success'
import { EmailVerificationPending } from './email-verification-pending'

const routeApi = getRouteApi('/_layout/_private/app/user/account')

export type ChangeEmailFormProps = {
  onClose: () => void
  email?: string | null
}

// Helper to check if verification email was sent within the last hour
const isWithinOneHour = (sentAt: string | null): boolean => {
  if (sentAt === null) {
    return false
  }

  const sentDate = new Date(sentAt)
  const now = new Date()
  const diffMs = now.getTime() - sentDate.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)

  return diffHours < 1
}

export const ChangeEmailForm = ({ onClose, email }: ChangeEmailFormProps) => {
  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()
  const { isInitialized, setEmailValidated } = useAuth()
  const { validate, validateField, errors, clearFieldError, isValid } =
    useFormValidation(passwordResetSchema)
  const { formatMessage } = useIntl()
  const [changeStatus, setChangeStatus] = useState<{
    status: 'warning' | 'success'
    message?: string
  } | null>(null)

  const [formValues, setFormValues] = useState<EmailChangeRequest>({
    email: email ?? '',
  })

  const { data: accountData } = useAccountRetrieve({
    query: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      placeholderData: keepPreviousData,
    },
  })

  const { mutate: confirmEmail, isPending: confirmingEmail } = useAccountEmailChangeConfirmCreate({
    mutation: {
      onSuccess: () => {
        setChangeStatus({
          status: 'success',
          message: formatMessage({ id: 'user.settings.email.confirm.success' }),
        })

        setEmailValidated(true)

        // Clear token from URL after showing success
        navigate({
          to: search.back_url ?? '.',
          replace: true,
          search: (prev) => ({
            ...prev,
            token: undefined,
          }),
        }).catch(console.error)
      },
      onError: (error: AxiosError) => {
        setChangeStatus({
          status: 'warning',
          message:
            getAxiosErrorMessage(error) ||
            formatMessage({ id: 'user.settings.email.confirm.error' }),
        })
      },
    },
  })
  const { mutate: changeEmail, isPending } = useAccountEmailChangeCreate({
    mutation: {
      onSuccess: () => {
        setChangeStatus({
          status: 'success',
        })
      },
      onError: (error: AxiosError) => {
        setChangeStatus({
          status: 'warning',
          message:
            getAxiosErrorMessage(error) || formatMessage({ id: 'user.settings.email.error' }),
        })
      },
    },
  })

  const { mutate: resendEmail, isPending: isResending } = useAccountEmailVerifyResendCreate({
    mutation: {
      onSuccess: () => {
        setChangeStatus({
          status: 'success',
        })
      },
      onError: (error: AxiosError) => {
        setChangeStatus({
          status: 'warning',
          message:
            getAxiosErrorMessage(error) || formatMessage({ id: 'user.settings.email.error' }),
        })
      },
    },
  })
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }))

    setTimeout(() => {
      const isValidField = validateField(name as PasswordResetFormField, value)
      if (isValidField) {
        clearFieldError(name as PasswordResetFormField)
      }
    }, 300)
  }

  const onSubmit = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    const isValid = validate(formValues)
    if (isValid) {
      changeEmail({ data: formValues })
    }
  }

  const onResendEmail = () => {
    if (formValues.email) {
      setChangeStatus(null)
      resendEmail({ data: { email: formValues.email } })
    }
  }

  const onChangeEmailAddress = (newEmail: string) => {
    // Update form values - modal already called API and got success
    setFormValues({ email: newEmail })
  }

  // Pre-fill from the `email` prop the moment it arrives (it comes from the
  // parent's account query, so it can be undefined at mount and resolve later).
  // Done during render via the store-previous-prop pattern rather than an effect:
  // only seeds while the field is still untouched, so a resolving/refetching
  // query never clobbers what the user has typed.
  const [prevEmail, setPrevEmail] = useState(email)
  if (email !== prevEmail) {
    setPrevEmail(email)
    if (email !== null && email !== undefined && formValues.email === '') {
      setFormValues({ email })
    }
  }

  useEffect(
    function confirmEmailFromUrlToken() {
      if (search.token !== undefined && search.token.length > 0) {
        setChangeStatus(null)
        confirmEmail({
          data: { token: search.token },
        })
      }
    },
    [search.token, confirmEmail],
  )

  const isTokenPresented = search.token !== undefined && search.token.length > 0

  // Show verification pending if:
  // 1. Email was just sent successfully OR
  // 2. Email was sent within last hour (from account data)
  const shouldShowVerificationPending =
    (changeStatus?.status === 'success' && !isTokenPresented) ||
    (!isTokenPresented &&
      accountData !== undefined &&
      !accountData.email_verified &&
      accountData.verification_email_sent_at !== null &&
      isWithinOneHour(accountData.verification_email_sent_at))

  if (shouldShowVerificationPending) {
    // Use unconfirmed_email if it has a value, otherwise use email from accountData or formValues
    const emailToShow =
      accountData?.unconfirmed_email !== undefined && accountData.unconfirmed_email.length > 0
        ? accountData.unconfirmed_email
        : (accountData?.email ?? formValues.email)

    return (
      <EmailVerificationPending
        email={emailToShow}
        isResending={isPending || isResending}
        onChangeEmail={onChangeEmailAddress}
        onResend={onResendEmail}
      />
    )
  }

  if (changeStatus?.status === 'success' && isTokenPresented) {
    // Email confirmed successfully
    return (
      <EmailValidationSuccess
        onExploreDeals={() => {
          navigate({ to: '/app/rewards' }).catch(console.error)
        }}
      />
    )
  }

  if (changeStatus?.status === 'warning' && isTokenPresented) {
    // Email confirmation failed (expired/invalid token)
    return (
      <EmailValidationError
        onBackToAccount={onClose}
        onRequestNewLink={() => {
          setChangeStatus(null)
          navigate({
            to: '.',
            replace: true,
            search: (prev) => ({
              ...prev,
              token: undefined,
            }),
          }).catch(console.error)
        }}
      />
    )
  }

  if (confirmingEmail || !isInitialized) {
    return (
      <div className="-my-4">
        <Loader />
      </div>
    )
  }

  return (
    <div className="w-full">
      <FormContainer>
        {!isTokenPresented && (
          <TextInput
            error={errors.email}
            label="common.labels.email"
            input={{
              disabled: isPending,
              name: 'email',
              type: 'email',
              value: formValues.email,
              endAddon: <IconEmail />,
              onChange,
            }}
          />
        )}
        {changeStatus && !isTokenPresented && (
          <Alert
            heading={changeStatus.message}
            severity={changeStatus.status}
            onClose={() => {
              setChangeStatus(null)
            }}
          />
        )}
        {!isTokenPresented && (
          <div className="flex gap-4">
            <Button
              color="secondary"
              disabled={isPending}
              size="large"
              variant="contained"
              onClick={onClose}
            >
              <FormattedMessage id="common.controls.cancel" />
            </Button>

            <Button
              className="flex-[1_0_auto]"
              color="primary"
              loading={isPending}
              size="large"
              type="submit"
              variant="contained"
              disabled={
                !isValid || formValues.email.toLowerCase() === email?.toLowerCase() || isPending
              }
              onClick={onSubmit}
            >
              <FormattedMessage id="common.controls.SendValidationEmail" />
            </Button>
          </div>
        )}
        <Typography
          className="flex items-start gap-2"
          color="text.secondary"
          variant="body2"
        >
          <InformationCircleIcon
            style={{ verticalAlign: 'bottom', flex: '0 0 auto' }}
            width={22}
          />
          <span>
            <FormattedMessage id="user.settings.email.description" />
          </span>
        </Typography>
      </FormContainer>
    </div>
  )
}
